import { useClient } from '@cortex/react-query';
import type { EvalMetricsTimelinePoint, Experiment } from '@cortex/types';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { EmptyState } from '../../../components/common/EmptyState';
import { PageHeader } from '../../../components/common/PageHeader';
import { Spinner } from '../../../components/common/Spinner';
import {
  ExperimentCard,
  ExperimentDetailModal,
} from '../../../components/experiments/ExperimentsList';
import { PreferencesPanel } from '../../../components/preferences/PreferencesPanel';
import { WorkflowPreferenceBadge } from '../../../components/preferences/WorkflowPreferenceBadge';
import { RefinementModelSelector } from '../../../components/refinement/RefinementModelSelector';
import { RefinementRunButton } from '../../../components/refinement/RefinementRunButton';
import { WorkflowEvalCostEstimate } from '../../../components/refinement/WorkflowEvalCostEstimate';
import {
  WorkflowMetricsChart,
  type IMetricTargets,
} from '../../../components/refinement/WorkflowMetricsChart';
import { pendingCountByWorkflow } from '../../../components/refinement-memory/pending-by-workflow';
import { useRefinementMemoryList } from '../../../lib/refinement-memory';
import { useTheopolis, type IResolvedRefinementPreference } from '../../../lib/theopolis';

export const Route = createFileRoute('/refinement/overview/')({ component: RefinementOverview });

/**
 * /refinement/overview — the refinement homepage. Groups every experiment
 * the refinement agent has produced by its **parent workflow**. Each
 * workflow section is split into:
 *
 *   1. Suggestions — experiments whose hypothesis was confirmed by the
 *      AI grader (`hypothesisSatisfied === 'SATISFIED'`). These are the
 *      changes the system thinks are worth promoting; surfaced first so
 *      they're easy to find.
 *
 *   2. Experiments — everything else, bucketed by lifecycle stage
 *      (awaiting review → active → reviewed).
 *
 * Workflows with zero experiments are hidden — the page is scoped to
 * what the agent has actually touched.
 */
function RefinementOverview() {
  const client = useClient();
  const theo = useTheopolis();
  const experimentsQuery = useQuery({
    queryKey: ['cortex', 'experiments'],
    queryFn: () => client.http.get('v1/experiments').json<Experiment[]>(),
  });
  const workflowsQuery = useQuery({
    queryKey: ['workflows', 'name-lookup'],
    queryFn: () =>
      client.http
        .get('v1/workflows?limit=200')
        .json<{ items: Array<{ id: string; name: string }> }>(),
    staleTime: 60_000,
  });
  const workflowOptions = workflowsQuery.data?.items ?? [];

  // Shared modal state — every card on the page opens into the same
  // detail modal regardless of which workflow section it lives in.
  const [openExperimentId, setOpenExperimentId] = useState<string | null>(null);
  const openExperiment = experimentsQuery.data?.find((e) => e.id === openExperimentId) ?? null;

  const workflowGroups = useMemo(
    () => groupByWorkflow(experimentsQuery.data ?? []),
    [experimentsQuery.data],
  );

  // Per-workflow accuracy/cost/speed history for the trend chart in each
  // section header. One lean query per workflow against the dedicated
  // metrics-timeline endpoint (cost is aggregated server-side — it isn't on
  // the evaluation row). Keyed by workflowId so React Query dedupes/caches
  // independently of the experiments list.
  const metricsQueries = useQueries({
    queries: workflowGroups.map((g) => ({
      queryKey: ['eval-metrics-timeline', g.workflowId],
      queryFn: () =>
        client.http
          .get(`v1/evaluations/metrics-timeline?workflowId=${g.workflowId}`)
          .json<EvalMetricsTimelinePoint[]>(),
      staleTime: 30_000,
    })),
  });
  // Per-workflow resolved preference (override → org → default). Shares the
  // query key with each WorkflowPreferenceBadge so React Query dedupes the
  // fetch; here we only need the targets to draw the chart's threshold lines.
  const prefsQueries = useQueries({
    queries: workflowGroups.map((g) => ({
      queryKey: ['theopolis', 'preferences', 'resolved', g.workflowId],
      queryFn: () =>
        theo
          .get(`preferences/refinement/resolved?workflowId=${encodeURIComponent(g.workflowId)}`)
          .json<IResolvedRefinementPreference>(),
      staleTime: 30_000,
    })),
  });
  const targetsByWorkflow = useMemo(() => {
    const m = new Map<string, IMetricTargets>();
    workflowGroups.forEach((g, i) => {
      const d = prefsQueries[i]?.data;
      m.set(g.workflowId, {
        // Chart accuracy is a 0–1 fraction; the stored target is a percentage.
        accuracy: d?.targetAccuracy != null ? d.targetAccuracy / 100 : null,
        cost: d?.targetCost ?? null,
        speed: d?.targetSpeed ?? null,
      });
    });
    return m;
  }, [workflowGroups, prefsQueries]);

  // Per-workflow pending memory count — drives the "X pending" pill in
  // each WorkflowSection header (DEV-16616 / Phase 3). One coarse fetch
  // for all pending memories in the org; we group client-side because
  // the active set is capped at 50 per scope and the workflow list
  // here is typically small. Sharing the `['theopolis',
  // 'refinement-memories']` key family means approving on the
  // standalone page or in-thread auto-invalidates this count.
  const pendingMemoriesQuery = useRefinementMemoryList({ status: 'pending' });
  const pendingCountByWorkflowMap = useMemo(
    () => pendingCountByWorkflow(pendingMemoriesQuery.data ?? []),
    [pendingMemoriesQuery.data],
  );

  const metricsByWorkflow = useMemo(() => {
    const m = new Map<
      string,
      { data: EvalMetricsTimelinePoint[] | undefined; loading: boolean; error: boolean }
    >();
    workflowGroups.forEach((g, i) => {
      const q = metricsQueries[i];
      m.set(g.workflowId, {
        data: q?.data,
        loading: q?.isLoading ?? false,
        error: Boolean(q?.error),
      });
    });
    return m;
  }, [workflowGroups, metricsQueries]);

  // Per-workflow expand/collapse state. Stored as an override map; any
  // workflow not in the map falls back to the default (expanded — cards
  // are open on first render so runs are immediately visible). The
  // override is keyed by `workflowId` so adding/removing workflows from
  // the page doesn't drop a user's interaction.
  const [collapseOverrides, setCollapseOverrides] = useState<Record<string, boolean>>({});
  const isExpanded = (workflowId: string): boolean => collapseOverrides[workflowId] ?? true;
  const toggleWorkflow = (workflowId: string): void => {
    setCollapseOverrides((prev) => ({
      ...prev,
      [workflowId]: !(prev[workflowId] ?? true),
    }));
  };
  // Aggregate state for the bulk-toggle control. "all" means every
  // workflow is currently open; "none" means every workflow is closed;
  // "mixed" otherwise. The button label flips based on which action
  // would be most useful next.
  const expandedCount = workflowGroups.filter((g) => isExpanded(g.workflowId)).length;
  const allExpanded = workflowGroups.length > 0 && expandedCount === workflowGroups.length;
  const setAll = (next: boolean): void => {
    const map: Record<string, boolean> = {};
    for (const g of workflowGroups) map[g.workflowId] = next;
    setCollapseOverrides(map);
  };

  return (
    <div>
      <PageHeader
        title="Monitor and Configure Refinement"
        actions={<RefinementRunButton workflows={workflowOptions} />}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 'var(--sp-4)',
          alignItems: 'start',
          marginBottom: 'var(--sp-4)',
        }}
      >
        <div>
          {experimentsQuery.isLoading ? (
            <Spinner />
          ) : experimentsQuery.error ? (
            <div className="alert alert--danger" style={{ marginTop: 'var(--sp-3)' }}>
              Failed to load experiments: {(experimentsQuery.error as Error).message}
            </div>
          ) : workflowGroups.length === 0 ? (
            <EmptyState
              title="No refinement activity yet"
              description="The refinement agent hasn't created any experiments. When it does, they'll appear here grouped by workflow."
            />
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setAll(!allExpanded)}
                  title={allExpanded ? 'Collapse every workflow' : 'Expand every workflow'}
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </button>
              </div>
              <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
                {workflowGroups.map((g) => (
                  <WorkflowSection
                    key={g.workflowId}
                    group={g}
                    metrics={metricsByWorkflow.get(g.workflowId)}
                    targets={targetsByWorkflow.get(g.workflowId)}
                    pendingMemoryCount={pendingCountByWorkflowMap.get(g.workflowId) ?? 0}
                    expanded={isExpanded(g.workflowId)}
                    onToggle={() => toggleWorkflow(g.workflowId)}
                    onOpenExperiment={setOpenExperimentId}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <PreferencesPanel />
          {/* Org-level model override — global for the refinement agent. */}
          <RefinementModelSelector />
        </div>
      </div>

      <ExperimentDetailModal
        experiment={openExperiment}
        onClose={() => setOpenExperimentId(null)}
      />
    </div>
  );
}

// ─── Workflow section ─────────────────────────────────────────────────────

interface IWorkflowGroup {
  workflowId: string;
  workflowName: string;
  /** Experiments where `hypothesisSatisfied === 'SATISFIED'`. Promoted. */
  suggestions: Experiment[];
  /** Everything else — bucketed inside `OtherExperiments`. */
  otherExperiments: Experiment[];
}

function WorkflowSection({
  group,
  metrics,
  targets,
  pendingMemoryCount,
  expanded,
  onToggle,
  onOpenExperiment,
}: {
  group: IWorkflowGroup;
  metrics:
    | { data: EvalMetricsTimelinePoint[] | undefined; loading: boolean; error: boolean }
    | undefined;
  targets: IMetricTargets | undefined;
  /** Count of workflow-scoped pending memories awaiting operator review. */
  pendingMemoryCount: number;
  expanded: boolean;
  onToggle: () => void;
  onOpenExperiment: (id: string) => void;
}) {
  const totalCount = group.suggestions.length + group.otherExperiments.length;
  // "Running" specifically tracks the literal RUNNING lifecycle status —
  // experiments whose evals are in flight right now. PENDING (queued)
  // and FAILED are surfaced on the cards themselves, not here.
  const runningCount =
    group.otherExperiments.filter((e) => e.status === 'RUNNING').length +
    group.suggestions.filter((e) => e.status === 'RUNNING').length;
  const headerId = `workflow-${group.workflowId}-header`;
  const bodyId = `workflow-${group.workflowId}-body`;

  return (
    <section
      style={{
        border: '1px solid var(--border-subtle)',
        borderRadius: 8,
        padding: 'var(--sp-3)',
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? 'var(--sp-3)' : 0,
        background: 'var(--surface-1, transparent)',
        position: 'relative',
      }}
    >
      {/* Memory pill — rendered as a sibling of the toggle button (not a
          descendant) because `<Link>` inside a `<button>` is invalid HTML.
          Absolute positioning anchors it to the header's right edge without
          contending with the button's `width: 100%`. Only renders when
          there are pending workflow-scoped memories to surface. */}
      {pendingMemoryCount > 0 ? (
        <Link
          to="/agents/memories"
          search={{ status: 'pending', workflowId: group.workflowId }}
          title={`${pendingMemoryCount} pending memor${
            pendingMemoryCount === 1 ? 'y' : 'ies'
          } for this workflow — click to review`}
          className="t-mono t-xs"
          style={{
            position: 'absolute',
            top: 'var(--sp-3)',
            right: 'var(--sp-3)',
            padding: '1px 6px',
            borderRadius: 10,
            color: 'var(--terminal-amber)',
            border:
              '1px solid color-mix(in oklab, var(--terminal-amber) 40%, var(--border-subtle))',
            background: 'color-mix(in oklab, var(--terminal-amber) 14%, transparent)',
            textDecoration: 'none',
            zIndex: 1,
          }}
        >
          {pendingMemoryCount} pending memor{pendingMemoryCount === 1 ? 'y' : 'ies'}
        </Link>
      ) : null}

      <button
        id={headerId}
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={bodyId}
        style={{
          // Whole-header click target — the chevron, name, counts, id, and
          // suggestion pill are all part of one tappable region.
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-2)',
          flexWrap: 'wrap',
          background: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
          // Reserve space at the right edge so the workflow-id <code> below
          // (`marginLeft: auto`) doesn't slide under the absolute-positioned
          // memory pill when one is rendered.
          paddingRight: pendingMemoryCount > 0 ? '160px' : 0,
        }}
      >
        <span
          aria-hidden={true}
          style={{
            display: 'inline-block',
            width: 14,
            color: 'var(--text-secondary)',
            // Lift the chevron a hair so it baselines with the H2.
            transform: 'translateY(-1px)',
          }}
        >
          {expanded ? '▾' : '▸'}
        </span>
        <h2 style={{ margin: 0, fontSize: 18 }}>{group.workflowName}</h2>
        <span className="t-secondary t-sm">
          {totalCount} experiment{totalCount === 1 ? '' : 's'}
        </span>
        {runningCount > 0 ? (
          <span
            className="t-mono t-xs"
            title={`${runningCount} experiment${
              runningCount === 1 ? ' has' : 's have'
            } evals in flight right now`}
            style={{
              padding: '1px 6px',
              borderRadius: 10,
              color: 'var(--status-info)',
              border: '1px solid var(--status-info-bd)',
              background: 'var(--status-info-bg)',
              // Tiny pulse-ish accent — communicates "live activity" without
              // animating, which would fight the rest of the page.
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span
              aria-hidden={true}
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'var(--status-info)',
              }}
            />
            {runningCount} running
          </span>
        ) : null}
        {group.suggestions.length > 0 ? (
          <span
            className="t-mono t-xs"
            title={`${group.suggestions.length} satisfied — surfaced as suggestions`}
            style={{
              padding: '1px 6px',
              borderRadius: 10,
              color: 'var(--status-success)',
              border: '1px solid var(--status-success-bd)',
              background: 'var(--status-success-bg)',
            }}
          >
            {group.suggestions.length} suggestion{group.suggestions.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <code
          className="t-mono t-xs t-secondary"
          style={{ marginLeft: 'auto' }}
          title="Workflow id"
        >
          {group.workflowId}
        </code>
      </button>

      {/* Always-visible metric trend — accuracy/cost/speed across every eval
          this workflow has run, independent of the collapse state below. The
          per-workflow preference sits to its right: a compact resolved-weight
          triangle that opens an editor for this workflow's override. */}
      <div
        style={{
          marginTop: 'var(--sp-2)',
          display: 'flex',
          gap: 'var(--sp-3)',
          alignItems: 'stretch',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <WorkflowMetricsChart
            timeline={metrics?.data}
            loading={metrics?.loading ?? false}
            error={metrics?.error ?? false}
            targets={targets}
          />
        </div>
        <WorkflowPreferenceBadge workflowId={group.workflowId} workflowName={group.workflowName} />
      </div>

      {expanded ? (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
        >
          {/* Lazy: the estimate query is gated on `expanded`, so collapsed
              sections never pay for it. Mounted inside the body so it reads
              as part of the workflow's expanded detail. */}
          <WorkflowEvalCostEstimate workflowId={group.workflowId} enabled={expanded} />

          {group.suggestions.length > 0 ? (
            <SuggestionsSection
              experiments={group.suggestions}
              onOpenExperiment={onOpenExperiment}
            />
          ) : null}

          {group.otherExperiments.length > 0 ? (
            <OtherExperimentsSection
              experiments={group.otherExperiments}
              onOpenExperiment={onOpenExperiment}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Suggestions — satisfied experiments, promoted to a highlighted strip
 * at the top of the workflow section. The card list itself reuses
 * `ExperimentCard`; the section wrapper carries the visual emphasis.
 */
function SuggestionsSection({
  experiments,
  onOpenExperiment,
}: {
  experiments: Experiment[];
  onOpenExperiment: (id: string) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--status-success-bd)',
        borderRadius: 6,
        padding: 'var(--sp-3)',
        background: 'var(--status-success-bg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-2)',
        }}
      >
        <SuggestionsIcon />
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--status-success)',
          }}
        >
          Suggestions
        </h3>
        <span className="t-secondary t-xs">
          {experiments.length} hypothesis-satisfied experiment
          {experiments.length === 1 ? '' : 's'}
        </span>
      </div>
      <p className="t-secondary t-xs" style={{ margin: 0, maxWidth: '70ch' }}>
        Experiments whose `hypothesisOfEffectOfChange` was confirmed by the AI grader — the
        refinement agent recommends promoting these.
      </p>
      <CardGrid experiments={experiments} onOpen={onOpenExperiment} />
    </div>
  );
}

/**
 * The non-suggestion experiments, bucketed by lifecycle stage so the
 * surface mirrors the existing /refinement (workflows-list) page.
 */
function OtherExperimentsSection({
  experiments,
  onOpenExperiment,
}: {
  experiments: Experiment[];
  onOpenExperiment: (id: string) => void;
}) {
  const awaitingReview = experiments.filter((e) => e.status === 'WAITING_FOR_HUMAN_INPUT');
  const inFlight = experiments.filter(
    (e) => e.status === 'PENDING' || e.status === 'RUNNING' || e.status === 'FAILED',
  );
  const reviewed = experiments.filter(
    (e) => e.status === 'HUMAN_ACCEPTED' || e.status === 'HUMAN_REJECTED',
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
        Experiments
        <span className="t-secondary t-xs" style={{ marginLeft: 'var(--sp-2)', fontWeight: 400 }}>
          {experiments.length}
        </span>
      </h3>

      {awaitingReview.length > 0 ? (
        <Bucket
          title="Awaiting review"
          subtitle="Evals finished — waiting on a human to accept or reject."
          experiments={awaitingReview}
          onOpen={onOpenExperiment}
        />
      ) : null}

      {inFlight.length > 0 ? (
        <Bucket
          title="Active"
          subtitle="Still pending, running, or recently failed."
          experiments={inFlight}
          onOpen={onOpenExperiment}
        />
      ) : null}

      {reviewed.length > 0 ? (
        <Bucket
          title="Reviewed"
          subtitle="Already accepted or rejected by a human."
          experiments={reviewed}
          onOpen={onOpenExperiment}
        />
      ) : null}
    </div>
  );
}

function Bucket({
  title,
  subtitle,
  experiments,
  onOpen,
}: {
  title: string;
  subtitle?: string;
  experiments: Experiment[];
  onOpen: (id: string) => void;
}) {
  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 'var(--sp-2)',
          marginBottom: 4,
        }}
      >
        <h4 style={{ margin: 0, fontSize: 12, fontWeight: 600 }}>{title}</h4>
        <span className="t-secondary t-xs">{experiments.length}</span>
      </header>
      {subtitle ? (
        <p className="t-secondary t-xs" style={{ margin: '0 0 var(--sp-2) 0' }}>
          {subtitle}
        </p>
      ) : null}
      <CardGrid experiments={experiments} onOpen={onOpen} />
    </div>
  );
}

function CardGrid({
  experiments,
  onOpen,
}: {
  experiments: Experiment[];
  onOpen: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 'var(--sp-2)',
      }}
    >
      {experiments.map((exp) => (
        <ExperimentCard key={exp.id} experiment={exp} onOpen={() => onOpen(exp.id)} />
      ))}
    </div>
  );
}

function SuggestionsIcon() {
  // Small "spark" mark — communicates the celebratory / recommended nature
  // of the section without leaning on platform-specific emoji.
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={true}
      style={{ color: 'var(--status-success)' }}
    >
      <polygon points="12 2 14.4 9 22 9.5 16 14.5 18 22 12 17.8 6 22 8 14.5 2 9.5 9.6 9" />
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function groupByWorkflow(experiments: Experiment[]): IWorkflowGroup[] {
  const byId = new Map<string, IWorkflowGroup>();
  for (const exp of experiments) {
    let g = byId.get(exp.workflowId);
    if (!g) {
      g = {
        workflowId: exp.workflowId,
        workflowName: exp.workflowName,
        suggestions: [],
        otherExperiments: [],
      };
      byId.set(exp.workflowId, g);
    }
    if (exp.hypothesisSatisfied === 'SATISFIED') {
      g.suggestions.push(exp);
    } else {
      g.otherExperiments.push(exp);
    }
  }
  // Sort experiments inside each bucket by attention priority so the
  // most-actionable ones appear first.
  for (const g of byId.values()) {
    g.suggestions.sort(byAttentionPriority);
    g.otherExperiments.sort(byAttentionPriority);
  }
  // Workflows themselves are ordered by their most-recent experiment so
  // freshly-touched workflows surface to the top.
  return [...byId.values()].sort((a, b) => {
    const at = mostRecentCreatedAt(a);
    const bt = mostRecentCreatedAt(b);
    return bt - at;
  });
}

function mostRecentCreatedAt(g: IWorkflowGroup): number {
  let max = 0;
  for (const e of g.suggestions) {
    max = Math.max(max, new Date(e.createdAt).getTime());
  }
  for (const e of g.otherExperiments) {
    max = Math.max(max, new Date(e.createdAt).getTime());
  }
  return max;
}

/**
 * Within a bucket, order by what most needs the user's attention.
 * WAITING_FOR_HUMAN_INPUT tops the list (a verdict is owed), then
 * in-flight states, then reviewed (history). Within a tier, newer first.
 */
function byAttentionPriority(a: Experiment, b: Experiment): number {
  const t = (s: Experiment['status']): number => {
    switch (s) {
      case 'WAITING_FOR_HUMAN_INPUT':
        return 0;
      case 'RUNNING':
        return 1;
      case 'PENDING':
        return 2;
      case 'FAILED':
        return 3;
      case 'HUMAN_ACCEPTED':
        return 4;
      case 'HUMAN_REJECTED':
        return 5;
      default:
        return 6;
    }
  };
  const dt = t(a.status) - t(b.status);
  if (dt !== 0) return dt;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
